import { Button, buttonVariants } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Webhook, Siren } from 'lucide-react';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { useIntegrations, useCreateIntegration } from '@/hooks/integrations';
import { useToast } from '@/hooks/use-toast';
import {
  DeviceType,
  IntegrationLimitedDto,
  SourceType,
} from '@/lib/integrationTypes/integrationTypes';
import { enumKeys, Comparator } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableFilterBar,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
} from '@/components/data-table';
import {
  FilterField,
  FilterForm,
  FilterHeader,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import { ScrollArea } from '@/components/ui/scroll-area';

interface IntegrationTableData {
  id: string;
  name: string;
  key: string;
  integrationType: string;
  brand: string;
  updated: string;
  sources: {
    smtp: boolean;
    webhook: boolean;
    lifecycle: boolean;
  };
}

function extractIntegrationTableData(integrations: IntegrationLimitedDto[]): IntegrationTableData[] {
  return integrations.map((integration) => ({
    id: integration.id || '',
    name: integration.name || '',
    key: integration.key || '',
    integrationType: integration.integrationType || '',
    brand: integration.brand || 'Not Specified',
    updated: integration.updated,
    sources: {
      smtp: integration.smtpSource,
      webhook: integration.webhookSource,
      lifecycle: integration.watchfulLifecycleTrigger,
    },
  }));
}

const formSchema = z.object({
  name: z.string().min(1, 'Integration name is required'),
  integrationType: z.string().min(1, 'Integration type is required'),
  brand: z.string().min(1, 'Brand is required'),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const IntegrationHub = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    data: integrationsResponse,
    error: integrationsError,
    isLoading: integrationsLoading,
  } = useIntegrations();
  const { mutateAsync: createIntegration } = useCreateIntegration({ queryClient });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      integrationType: DeviceType[DeviceType.Nvr],
      brand: '',
      description: '',
    },
  });

  const [urlParams, setUrlParams] = useQueryStates(
    {
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(50),
      name: parseAsString,
      integrationType: parseAsString,
      filters: parseAsString,
    },
    { history: 'replace' }
  );

  const page = urlParams.page;
  const pageSizeParam = urlParams.pageSize;
  const nameFilter = urlParams.name || '';
  const typeFilter = urlParams.integrationType || 'all';
  const filtersOpen = urlParams.filters === 'open';

  useEffect(() => {
    if (integrationsError) {
      toast({
        variant: 'destructive',
        title: 'Could not fetch integrations',
        description: integrationsError.message,
      });
    }
  }, [integrationsError, toast]);

  const filteredIntegrations = useMemo(() => {
    if (!integrationsResponse?.integrations) return [];
    return integrationsResponse.integrations
      .filter((integration) => {
        const matchesName =
          !nameFilter || integration.name.toLowerCase().includes(nameFilter.toLowerCase());
        const matchesType =
          typeFilter === 'all' ||
          typeFilter === undefined ||
          integration.integrationType === typeFilter;
        return matchesName && matchesType;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [integrationsResponse, nameFilter, typeFilter]);

  const integrationTableData = useMemo(() => {
    return extractIntegrationTableData(filteredIntegrations);
  }, [filteredIntegrations]);

  const integrationColumns: ColumnDef<IntegrationTableData>[] = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'name',
      enableHiding: false,
      meta: { label: 'Name' },
      header: 'Name',
      cell: ({ row }) => {
        const { name, id } = row.original;
        return (
          <Link 
            to={`/ihub/integration/${id}`} 
            className="font-semibold text-lime hover:underline"
          >
            {name || <span className="text-muted">UNAMED</span> }
          </Link>
        );
      },
    },
    {
      id: 'status',
      accessorKey: 'updated',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) => {
        const updated = row.original.updated;
        let relativeTime = 'Unknown';
        if (updated) {
          try {
            relativeTime = formatDistanceToNow(new Date(updated), { addSuffix: true });
          } catch (e) {
            console.error(e);
          }
        }
        return (
          <div className="flex items-center gap-4">
            <div className="h-2 w-2 rounded-full bg-green-400" />
            <div>
              <span className="block text-gray-400">Last updated {relativeTime}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'integrationType',
      accessorKey: 'integrationType',
      meta: { label: 'Type' },
      header: 'Type',
      cell: ({ row }) => {
        const type = row.original.integrationType;
        return type || <span className="text-gray-600">--</span>;
      },
    },
    {
      id: 'sources',
      meta: { label: 'Sources' },
      header: 'Sources',
      cell: ({ row }) => {
        const { sources } = row.original;

        return (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2">
              {sources.smtp && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Mail className="h-5 w-5 text-white" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>SMTP Events</span>
                  </TooltipContent>
                </Tooltip>
              )}
              {sources.webhook && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Webhook className="h-5 w-5 text-white" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Webhook Events</span>
                  </TooltipContent>
                </Tooltip>
              )}
              {sources.lifecycle && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Siren className="h-5 w-5 text-white" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span>Watchful Lifecycle Events</span>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        );
      },
    },
    {
      id: 'brand',
      accessorKey: 'brand',
      meta: { label: 'Brand' },
      header: 'Brand',
      cell: ({ row }) => {
        const brand = row.original.brand;
        return brand || <span className="text-gray-600">Not Specified</span>;
      },
    },
    {
      id: 'key',
      accessorKey: 'key',
      meta: { label: 'Key' },
      header: 'Key',
      cell: ({ row }) => {
        const key = row.original.key;
        return key || <span className="text-gray-600">--</span>;
      },
    },
  ], []);

  return (
    <div className="relative flex h-full flex-col">
      <DataTable
        columns={integrationColumns}
        data={integrationTableData}
        loading={integrationsLoading}
        error={integrationsError as Error | null}
        page={page}
        pageSize={integrationTableData.length || pageSizeParam}
        totalCount={integrationTableData.length}
        onPageChange={(p) => setUrlParams({ page: p })}
        onPageSizeChange={(ps) => setUrlParams({ pageSize: ps, page: 1 })}
        pageSizeOptions={[integrationTableData.length || 50]}
        stateKey="integrations-table"
        emptyMessage="No integrations found"
        sidebarOpen={filtersOpen}
        onSidebarOpenChange={(open) => setUrlParams({ filters: open ? 'open' : null })}
      >
        <FilterForm
          key={`integration-filters-${typeFilter}`}
          defaultValues={{
            integrationType: typeFilter || 'all',
          }}
          onFiltersChange={({ values }) => {
            setUrlParams({
              integrationType: (values.integrationType as string) || 'all',
              page: 1,
            });
          }}
        >
          <DataTableContainer>
            <DataTableFilterBar>
              <div className="flex h-full flex-col">
                <FilterHeader onClose={() => setUrlParams({ filters: null })} />
                <ScrollArea style={{ height: 'calc(100vh - 128px)' }} className="flex-1 px-4">
                  <div className="space-y-4 pb-4 pt-2">
                    <FilterInlineResetButton
                      onReset={() =>
                        setUrlParams({
                          integrationType: 'all',
                          page: 1,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-4 pb-4">
                    <FilterField
                      type="toggle"
                      name="integrationType"
                      label="Type"
                      field="integrationType"
                      comparator={Comparator.Eq}
                      options={[{ label: 'All', value: 'all' }].concat(
                        enumKeys(DeviceType).map((d) => ({ label: d, value: d }))
                      )}
                    />
                  </div>
                </ScrollArea>
              </div>
            </DataTableFilterBar>
            <DataTableContent>
              <DataTableUtilityBar
                showFilterToggle
                showColumnVisibility
                filterCount={[typeFilter !== 'all' ? typeFilter : ''].filter(Boolean).length}
                search={{
                  value: nameFilter,
                  onChange: (value) => setUrlParams({ name: value || null, page: 1 }),
                  placeholder: 'Search integrations...',
                }}
                actions={
                  <div className="flex gap-2">
                    <Link to="/ihub/monitor" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                      Monitor
                    </Link>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button size="sm">
                          Add Integration
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="bg-[#111]" align="end">
                        <Form {...form}>
                          <form
                            className="flex flex-col gap-5"
                            onSubmit={form.handleSubmit(({ integrationType, name, description, brand }) =>
                              createIntegration({
                                name,
                                description,
                                sourceType: SourceType.Code,
                                integrationType: DeviceType[integrationType as keyof typeof DeviceType],
                                brand,
                              })
                                .then((res) => {
                                  toast({ title: 'Created new integration' });
                                  const newId = res?.data?.id;
                                  if (newId) {
                                    navigate(`/ihub/integration/${newId}`);
                                  } else {
                                    console.error('Could not find ID for new integration', res);
                                  }
                                })
                                .catch((ex) => {
                                  console.error(ex);
                                  toast({
                                    title: 'Error creating integration',
                                    description: ex.message,
                                    variant: 'destructive',
                                  });
                                })
                            )}
                          >
                            <FormField
                              control={form.control}
                              name="name"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Integration name</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="brand"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Brand</FormLabel>
                                  <FormControl>
                                    <Input {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="integrationType"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Integration Type</FormLabel>
                                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select an integration type" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {enumKeys(DeviceType).map((deviceType) => (
                                        <SelectItem key={deviceType} value={deviceType}>
                                          {deviceType}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="description"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Description</FormLabel>
                                  <FormControl>
                                    <Textarea {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <Button type="submit" className="">
                              Create
                            </Button>
                          </form>
                        </Form>
                      </PopoverContent>
                    </Popover>
                  </div>
                }
              />
              <DataTableWrapper>
                <DataTableHeader />
                <DataTableRows />
              </DataTableWrapper>
              <DataTableFooter />
            </DataTableContent>
          </DataTableContainer>
        </FilterForm>
      </DataTable>
      <div className="border-t bg-background/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground min-w-0 flex-1">
            Showing {integrationTableData.length} results
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationHub;